import {
  Flex,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
} from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  IMAGE_COUNT_OPTIONS,
  ImageCount,
  ImageCountButton,
} from "@/features/order-sheet-sets/order-sheet-product-gallery";
import AppButton from "@/features/line-sheet-sets/app-button";
import { PathPickProductCardComparisonLayout } from "@/features/pathpick/types";

const COMPARISON_LAYOUTS: PathPickProductCardComparisonLayout[] = [
  "side",
  "stack",
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  imageCount: ImageCount;
  onImageCountChange: (imageCount: ImageCount) => void;
  showPriceDetail: boolean;
  onShowPriceDetailChange: (showPriceDetail: boolean) => void;
  isAllExpanded: boolean;
  onIsAllExpandedChange: (isAllExpanded: boolean) => void;
  // 크림·포이즌 시세 비교 배치 — 시세를 볼 수 있는 사용자(패스픽 멤버)에게만 넘어온다.
  marketComparisonLayout?: PathPickProductCardComparisonLayout;
  onMarketComparisonLayoutChange?: (
    layout: PathPickProductCardComparisonLayout
  ) => void;
}

/**
 * 카드 보기에서 무엇을 보여줄지 모아 놓은 설정.
 * 툴바에 늘어놓으면 자리를 다 먹어서 톱니 아이콘 뒤에 넣었다.
 */
export default function OrderSheetGallerySettingsModal({
  isOpen,
  onClose,
  imageCount,
  onImageCountChange,
  showPriceDetail,
  onShowPriceDetailChange,
  isAllExpanded,
  onIsAllExpandedChange,
  marketComparisonLayout,
  onMarketComparisonLayoutChange,
}: Props) {
  const { t, tTitle } = useI18nHelper();

  const rows: {
    id: string;
    label: string;
    isChecked: boolean;
    onChange: (checked: boolean) => void;
  }[] = [
    {
      id: "gallery_show_price_detail",
      label: tTitle("price_detail"),
      isChecked: showPriceDetail,
      onChange: onShowPriceDetailChange,
    },
    {
      id: "gallery_expand_options",
      label: tTitle("option_detail"),
      isChecked: isAllExpanded,
      onChange: onIsAllExpandedChange,
    },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered={true} size={"sm"}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text textStyle={"body-md-bold"}>{tTitle("card_view_settings")}</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody paddingBottom={"24px"}>
          <Flex flexDirection={"column"} gap={"16px"}>
            <Flex flexDirection={"column"} gap={"8px"}>
              <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                {tTitle("image")}
              </Text>
              <Flex gap={"4px"} alignItems={"center"}>
                {IMAGE_COUNT_OPTIONS.map((count) => (
                  <ImageCountButton
                    key={`image_count_${count}`}
                    count={count}
                    isActive={imageCount === count}
                    onClick={() => onImageCountChange(count)}
                  />
                ))}
              </Flex>
            </Flex>
            {rows.map((row) => (
              <FormControl
                key={row.id}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <FormLabel htmlFor={row.id} mb={"0"}>
                  <Text textStyle={"body-sm-regular"}>{row.label}</Text>
                </FormLabel>
                <Switch
                  id={row.id}
                  isChecked={row.isChecked}
                  onChange={(event) => row.onChange(event.target.checked)}
                />
              </FormControl>
            ))}
            {/* 패스픽 상품 카드 보기 설정과 같은 두 가지 배치 — 같은 문구를 쓴다. */}
            {marketComparisonLayout && onMarketComparisonLayoutChange && (
              <Flex flexDirection={"column"} gap={"8px"}>
                <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                  {t("market_comparison_layout")}
                </Text>
                <Flex gap={"8px"}>
                  {COMPARISON_LAYOUTS.map((layout) => (
                    <AppButton
                      key={layout}
                      variant={"white"}
                      size={"medium"}
                      isActive={marketComparisonLayout === layout}
                      onClick={() => onMarketComparisonLayoutChange(layout)}
                      chakraProps={{ flex: 1 }}
                    >
                      <Text textStyle={"body-sm-bold"}>
                        {t(`path_pick:card_view_settings.layout_${layout}`)}
                      </Text>
                    </AppButton>
                  ))}
                </Flex>
              </Flex>
            )}
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
